import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import ToggleColorMode from './ToggleColorMode';
import '@fontsource/audiowide';

import MultivrseLabLogo from '../../icons/multivrselab_logo.png';

function AppAppBar({ mode, toggleColorMode }) {
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const scrollToSection = (sectionId) => {
    const sectionElement = document.getElementById(sectionId);
    const offset = 128;
    if (sectionElement) {
      const targetScroll = sectionElement.offsetTop - offset;
      sectionElement.scrollIntoView({ behavior: 'smooth' });
      window.scrollTo({
        top: targetScroll,
        behavior: 'smooth',
      });
      setOpen(false);
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: { xs: 1, sm: 2 }, // Responsive margin-top
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          variant="regular"
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            borderRadius: '999px',
            bgcolor:
              theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.4)'
                : 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(24px)',
            maxHeight: { xs: 56, sm: 64 }, // Responsive toolbar height
            minHeight: { xs: 56, sm: 64 },
            border: '1px solid',
            borderColor: 'divider',
            boxShadow:
              theme.palette.mode === 'light'
                ? `0 0 1px rgba(85, 166, 246, 0.1), 1px 1.5px 2px -1px rgba(85, 166, 246, 0.15), 4px 4px 12px -2.5px rgba(85, 166, 246, 0.15)`
                : '0 0 1px rgba(2, 31, 59, 0.7), 1px 1.5px 2px -1px rgba(2, 31, 59, 0.65), 4px 4px 12px -2.5px rgba(2, 31, 59, 0.65)',
            px: { xs: 1, sm: 2 }, // Responsive padding
          })}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              ml: { xs: -1, sm: -2 }, // Responsive margin-left
              px: { xs: 1, sm: 0 },
            }}
          >
            <Box
              component="img"
              src={MultivrseLabLogo}
              alt="logo of Multivrselab"
              sx={{
                width: { xs: 30, sm: 40, md: 50 }, // Responsive logo size
                height: { xs: 30, sm: 40, md: 50 },
                maxWidth: '50px', // Cap size
                maxHeight: '50px',
                objectFit: 'contain',
                mr: { xs: 1, sm: 2 }, // Responsive margin-right
                ml: { xs: 1, sm: 2 }, // Responsive margin-right
                display: 'block',
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="h1"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
                  fontFamily: 'Audiowide',
                  fontWeight: 'lighter',
                  fontSize: { xs: 'clamp(20px, 5vw, 24px)', sm: 'clamp(26px, 5vw, 30px)' },
                }}
              >
                MULTI
              </Typography>
              <Typography
                component="span"
                variant="h1"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'error.main' : 'error.light',
                  fontFamily: 'Audiowide',
                  fontWeight: 'lighter',
                  fontSize: { xs: 'clamp(20px, 5vw, 24px)', sm: 'clamp(26px, 5vw, 30px)' },
                }}
              >
                VR
              </Typography>
              <Typography
                component="span"
                variant="h1"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
                  fontFamily: 'Audiowide',
                  fontWeight: 'lighter',
                  fontSize: { xs: 'clamp(20px, 5vw, 24px)', sm: 'clamp(26px, 5vw, 30px)' },
                }}
              >
                SELAB
              </Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: { xs: 2, sm: 4, md: 9 } }}>
              <MenuItem
                onClick={() => scrollToSection('products')}
                sx={{ py: { xs: '4px', md: '6px' }, px: { xs: '8px', md: '12px' } }}
              >
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ fontSize: { xs: '16px', md: '20px' }, m: { xs: '4px', md: '10px' } }}
                >
                  Products
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => scrollToSection('services')}
                sx={{ py: { xs: '4px', md: '6px' }, px: { xs: '8px', md: '12px' } }}
              >
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ fontSize: { xs: '16px', md: '20px' }, m: { xs: '4px', md: '10px' } }}
                >
                  Services
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => scrollToSection('demos')}
                sx={{ py: { xs: '4px', md: '6px' }, px: { xs: '8px', md: '12px' } }}
              >
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ fontSize: { xs: '16px', md: '20px' }, m: { xs: '4px', md: '10px' } }}
                >
                  Demos
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => scrollToSection('aboutus')}
                sx={{ py: { xs: '4px', md: '6px' }, px: { xs: '8px', md: '12px' } }}
              >
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ fontSize: { xs: '16px', md: '20px' }, m: { xs: '4px', md: '10px' } }}
                >
                  About
                </Typography>
              </MenuItem>
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' }, // Show ToggleColorMode on md+
              gap: { xs: 0.5, md: 1 },
              alignItems: 'center',
            }}
          >
            <ToggleColorMode mode={mode} toggleColorMode={toggleColorMode} />
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <Button
              variant="text"
              color="primary"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{ minWidth: '30px', p: '4px' }}
            >
              <MenuIcon />
            </Button>
            <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
              <Box
                sx={{
                  width: { xs: '75vw', sm: '60vw', md: '40vw' }, // Responsive drawer width
                  p: { xs: 1.5, sm: 2 },
                  backgroundColor: 'background.paper',
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: 2,
                  }}
                >
                  <ToggleColorMode mode={mode} toggleColorMode={toggleColorMode} />
                </Box>
                <MenuItem onClick={() => scrollToSection('products')}>
                  <Typography variant="body1" sx={{ fontSize: '18px' }}>
                    Products
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => scrollToSection('services')}>
                  <Typography variant="body1" sx={{ fontSize: '18px' }}>
                    Services
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => scrollToSection('demos')}>
                  <Typography variant="body1" sx={{ fontSize: '18px' }}>
                    Demos
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => scrollToSection('aboutus')}>
                  <Typography variant="body1" sx={{ fontSize: '18px' }}>
                    About
                  </Typography>
                </MenuItem>
                <Divider sx={{ my: 1 }} />
              </Box>
            </Drawer>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

AppAppBar.propTypes = {
  mode: PropTypes.oneOf(['dark', 'light']).isRequired,
  toggleColorMode: PropTypes.func.isRequired,
};

export default AppAppBar;